import { ITEM_LOCATION_TYPES, ROADMAP_REQUEST_FIELDS } from '@/roadmaps/constants/roadmaps.constants';
import useScroll from '@/roadmaps/use/use-scroll';
import * as api from '@/vuex/modules/roadmaps/api/roadmaps.api.js';
import { asyncQueue } from '@/vuex/modules/roadmaps/async-queue/async-queue';
import filter from 'lodash/filter';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';
import mapValues from 'lodash/mapValues';
import pick from 'lodash/pick';
import Vue from 'vue';

const state = {
    swimlanes: {},
};

const getters = {
    swimlanes: (state) => {
        return {
            ...state.swimlanes,
            ...asyncQueue.queuedUpdates.swimlanes,
        };
    },
    orderedSwimlanes: (_state, getters) => {
        return Object.values(getters.swimlanes).sort((a, b) => a.index - b.index);
    },
    swimlaneById: (_state, getters) => (swimlaneId) => getters.swimlanes[swimlaneId],
    swimlaneByItemId: (state, getters, _rootState, rootGetters) => (itemId) => {
        const item = rootGetters['roadmaps/items/itemById'](itemId);
        if (!item) return null;
        if (item.parentType === '') return null;
        if (item.parentType === ITEM_LOCATION_TYPES.SWIMLANE.key) return state.swimlanes[item.parentId];

        return getters.swimlaneByItemId(item.parentId);
    },
    swimlaneIds: (state) => Object.keys(state.swimlanes),
    swimlaneByIndex: (_state, getters) => (index) => getters.orderedSwimlanes[index],
    firstSwimlaneId: (_state, getters) => getters.orderedSwimlanes[0].id,
    emptyRowsInSwimlane: (_state, _getters, _rootState, rootGetters) => (swimlaneId) => {
        const itemsInSwimlane = rootGetters['roadmaps/items/itemsByParentId'](swimlaneId);
        if (itemsInSwimlane.length === 0) return [];

        const rowsWithItems = new Set(itemsInSwimlane.map((item) => item.row));
        const maxRow = Math.max(...rowsWithItems) + 1;

        const allRows = Array.from({ length: maxRow }, (_value, index) => index);

        return allRows.filter((row) => !rowsWithItems.has(row));
    },
};

const mutations = {
    setMap (state, { swimlanes }) {
        Vue.set(
            state,
            'swimlanes',
            mapValues(swimlanes, (swimlane) => Object.freeze(swimlane)),
        );
    },
};

const actions = {
    async updateSwimlanes ({ dispatch, rootGetters }, swimlanesToUpdate) {
        const task = async () => {
            const currentRoadmap = rootGetters['roadmaps/roadmaps/currentRoadmap'];
            const payload = {
                swimlanes: map(swimlanesToUpdate, (item) => {
                    return pick(item, ROADMAP_REQUEST_FIELDS.swimlane);
                }),
                roadmapVersionTimestamp: currentRoadmap.lastUpdatedAt,
            };
            const updatedRoadmap = await api.updateSwimlanes(currentRoadmap.id, payload);
            dispatch('roadmaps/roadmaps/setOne', updatedRoadmap, { root: true });

            const { resetHorizontalScrollSyncComponents } = useScroll();
            resetHorizontalScrollSyncComponents();

            return updatedRoadmap.swimlanes;
        };

        const swimlanesWithoutDeleted = filter(swimlanesToUpdate, (swimlane) => swimlane?.action !== 'delete');

        return asyncQueue.enqueue({
            task,
            updates: keyBy(swimlanesWithoutDeleted, 'id'),
            updateType: 'swimlanes',
        });
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
